import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Anchor,
  Box,
  Group,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Alert, BackButton, Button, Modal, Select as SharedSelect, Textarea } from '@shared/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IoCallOutline,
  IoCarOutline,
  IoChatbubbleOutline,
  IoDocumentTextOutline,
  IoMailOutline,
  IoOpenOutline,
  IoStar,
  IoTimeOutline,
} from 'react-icons/io5';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { SellerAdminStatusBadges } from '@modules/sellers/components/SellerAdminStatusBadge';
import { pickupPointsApi } from '@modules/pickup-points/api/pickup-points.api';
import {
  sellersApi,
  type SellerAccountBlockDto,
  type SellerAccountBlockReasonDto,
} from '@modules/sellers/api/sellers.api';
import {
  SELLER_ACCOUNT_BLOCK_REASON_LABELS,
  SELLER_ACCOUNT_BLOCK_SELECT_OPTIONS,
} from '@modules/sellers/seller-account-block.constants';
import {
  sellerApplicationsModerationApi,
  type SellerApplicationRejectionKind,
  type SellerApplicationRow,
} from '@modules/seller-applications/api/seller-applications-moderation.api';
import { SELLER_APPLICATION_REJECTION_OPTIONS } from '@modules/seller-applications/seller-application-rejection.constants';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import {
  sellerFinanceApi,
  type SellerFinancePayoutLineDto,
} from '@modules/seller-finance/api/seller-finance.api';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import { fileLabelFromUrl } from '@shared/utils/file-label-from-url';
import { openSignedDocument } from '@shared/utils/open-signed-document';
import { formatDate as formatIsoDate, formatPhoneNumber, formatPrice } from '@shared/utils';
import '@shared/styles/page.css';
import './SellerDetailPage.css';
import { SellerFinanceTab } from './SellerFinanceTab';
import { SellerProfileChangesSection } from './SellerProfileChangesSection';
import { ZoomableImage } from './ZoomableImage';

/** Как в кабинете продавца (SellerProfilePage). */
const ACCOUNT_LABELS: Record<string, string> = {
  LEGAL_ENTITY: 'Юридическое лицо (ООО / АО)',
  INDIVIDUAL_ENTREPRENEUR: 'Индивидуальный предприниматель (ИП)',
  SELF_EMPLOYED: 'Самозанятый',
};

function tbankSePartnerLabel(status: string | null | undefined): string {
  switch (status) {
    case 'ADD_SENT':
      return 'Отправляем в Т‑Банк';
    case 'DRAFT':
    case 'WAITING_NALOG':
      return 'Ждём подтверждение в «Мой налог»';
    case 'ACTIVE':
      return 'Подключён, можно платить';
    case 'ERROR':
      return 'Ошибка добавления в Т‑Банк';
    default:
      return 'Ещё не отправляли в Т‑Банк';
  }
}

const PROFILE_TABS_MAIN = [
  { value: 'application' as const, label: 'Заявка' },
  { value: 'requisites' as const, label: 'Реквизиты' },
  { value: 'documents' as const, label: 'Документы' },
] as const;

const PROFILE_TABS_SELLER_EXTRA = [
  { value: 'pickup-points' as const, label: 'Точки самовывоза' },
  { value: 'finance' as const, label: 'Финансы' },
] as const;

type ProfileTabMainValue = (typeof PROFILE_TABS_MAIN)[number]['value'];
type ProfileTabExtraValue = (typeof PROFILE_TABS_SELLER_EXTRA)[number]['value'];
type ProfileTabValue = ProfileTabMainValue | ProfileTabExtraValue;

function Row({
  label,
  value,
  unstyledValue,
}: {
  label: string;
  value: React.ReactNode;
  unstyledValue?: boolean;
}) {
  return (
    <div className="seller-profile-row">
      <Text size="sm" c="dimmed" className="seller-profile-row-label">
        {label}
      </Text>
      <Box className="seller-profile-row-value">
        {unstyledValue ? (
          value
        ) : (
          <Text size="sm" component="div">
            {value ?? '—'}
          </Text>
        )}
      </Box>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="seller-profile-card">
      {title ? (
        <div className="seller-profile-card__head">
          <h3 className="seller-profile-card__title">{title}</h3>
        </div>
      ) : null}
      <div className="seller-profile-card__body">{children}</div>
    </section>
  );
}

function reviewCountLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return `${count} отзывов`;
  if (n1 > 1 && n1 < 5) return `${count} отзыва`;
  if (n1 === 1) return `${count} отзыв`;
  return `${count} отзывов`;
}

function SellerShopRating({
  rating,
  reviewCount,
}: {
  rating?: number | null;
  reviewCount?: number;
}) {
  if (rating == null) {
    return <span className="seller-shop-rating seller-shop-rating--empty">Нет рейтинга</span>;
  }

  const value = Number(rating).toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const count = reviewCount ?? 0;

  return (
    <span className="seller-shop-rating" aria-label={`Рейтинг магазина ${value} из 5, ${reviewCountLabel(count)}`}>
      <span className="seller-shop-rating__value">{value}</span>
      <IoStar size={16} className="seller-shop-rating__star" aria-hidden />
      <span className="seller-shop-rating__count">{reviewCountLabel(count)}</span>
    </span>
  );
}

function SellerDetailLogo({ url }: { url: string }) {
  const [showLinkFallback, setShowLinkFallback] = useState(false);
  const src = devProxyS3Url(url) || url;
  if (showLinkFallback) {
    return (
      <Anchor href={url} target="_blank" rel="noopener noreferrer">
        Открыть файл
      </Anchor>
    );
  }
  return (
    <ZoomableImage
      src={src}
      alt="Логотип магазина"
      className="seller-profile-logo-preview"
      onError={() => setShowLinkFallback(true)}
    />
  );
}

/** Паспорт, ЕГРЮЛ и оферта лежат в закрытом бакете — открываем по временной ссылке с бэкенда. */
function openApplicationDocument(applicationId: number, url: string) {
  void openSignedDocument(() =>
    sellerApplicationsModerationApi.documentLink(applicationId, url).then((r) => r.url),
  ).catch(() => {
    notifications.show({ title: 'Ошибка', message: 'Не удалось открыть документ', color: 'red' });
  });
}

function DocTile({
  title,
  meta,
  href,
  applicationId,
}: {
  title: string;
  meta?: string | null;
  href?: string | null;
  applicationId: number | null;
}) {
  const url = href?.trim() ? href.trim() : null;
  if (!url || applicationId == null) {
    return (
      <div className="seller-profile-doc-tile seller-profile-doc-tile--empty" aria-disabled>
        <span className="seller-profile-doc-tile__icon" aria-hidden>
          <IoDocumentTextOutline size={22} />
        </span>
        <div className="seller-profile-doc-tile__text">
          <span className="seller-profile-doc-tile__title">{title}</span>
          <span className="seller-profile-doc-tile__meta">Не загружено</span>
        </div>
      </div>
    );
  }
  return (
    <a
      className="seller-profile-doc-tile"
      href="#"
      onClick={(event) => {
        event.preventDefault();
        openApplicationDocument(applicationId, url);
      }}
      title={meta ? `${title}: ${meta}` : title}
    >
      <span className="seller-profile-doc-tile__icon" aria-hidden>
        <IoDocumentTextOutline size={22} />
      </span>
      <div className="seller-profile-doc-tile__text">
        <span className="seller-profile-doc-tile__title">{title}</span>
        {meta ? <span className="seller-profile-doc-tile__meta">{meta}</span> : null}
      </div>
      <span className="seller-profile-doc-tile__open" aria-hidden>
        <IoOpenOutline size={16} />
      </span>
    </a>
  );
}

function DocTilesFromUrls({
  title,
  urls,
  applicationId,
}: {
  title: string;
  urls: string[];
  applicationId: number | null;
}) {
  if (!urls.length) return <DocTile title={title} applicationId={applicationId} />;
  return (
    <>
      {urls.map((url) => (
        <DocTile
          key={url}
          title={title}
          meta={fileLabelFromUrl(url)}
          href={url}
          applicationId={applicationId}
        />
      ))}
    </>
  );
}

function getText(d: Record<string, unknown>, key: string): string | null {
  const value = d[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function getStringArray(d: Record<string, unknown>, key: string): string[] {
  const value = d[key];
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      dateStyle: 'long',
    });
  } catch {
    return iso;
  }
}

function sellerAccountBlockReasonAdminLabel(
  reason: SellerAccountBlockReasonDto,
): string {
  return SELLER_ACCOUNT_BLOCK_REASON_LABELS[reason] ?? reason;
}

function sellerAccountBlockTimingLine(block: SellerAccountBlockDto): string {
  if (block.permanent) {
    return 'Срок: без ограничений (навсегда).';
  }
  if (block.awaitingManualReview) {
    return 'Срок: до выяснения обстоятельств';
  }
  if (block.blockedUntil) {
    try {
      const d = new Date(block.blockedUntil);
      return `Авторазблокировка: ${d.toLocaleString('ru-RU', {
        dateStyle: 'long',
        timeStyle: 'short',
      })}`;
    } catch {
      return 'Срок разблокировки уточняется.';
    }
  }
  return 'Срок автоматической разблокировки не задан.';
}

type SellerDetailLocationState = {
  returnToProductId?: number;
  returnToProductFrom?: string;
  from?: string;
  orderId?: number;
  tab?: ProfileTabValue;
};

/** Маркер итоговой строки (не конфликтует с id выплат из БД). */
const SELLER_PAYOUTS_TOTAL_ROW_ID = -1;

function getOtherDeductionValue(otherDeductions: unknown, key: string): number {
  if (!otherDeductions || typeof otherDeductions !== 'object') return 0;
  const raw = (otherDeductions as Record<string, unknown>)[key];
  const num =
    typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : 0;
  return Number.isFinite(num) ? num : 0;
}

function payoutPromotionDisplayed(p: SellerFinancePayoutLineDto): number {
  return (
    p.promotionDeduction + getOtherDeductionValue(p.otherDeductions, 'advertisingDeductionRub')
  );
}

function isPayoutTotalsRow(p: SellerFinancePayoutLineDto): boolean {
  return p.id === SELLER_PAYOUTS_TOTAL_ROW_ID;
}

function emphasizeTotal(p: SellerFinancePayoutLineDto, node: React.ReactNode): React.ReactNode {
  return isPayoutTotalsRow(p) ? (
    <Text fw={700} component="span">
      {node}
    </Text>
  ) : (
    node
  );
}

function buildSellerPayoutsTotalsRow(
  payouts: SellerFinancePayoutLineDto[],
  sellerId: number,
): SellerFinancePayoutLineDto {
  let ordersCount = 0;
  let itemsSold = 0;
  let grossRevenue = 0;
  let commissionAmount = 0;
  let promotionCombined = 0;
  let penalties = 0;
  let delivery = 0;
  let netAmount = 0;
  for (const p of payouts) {
    ordersCount += p.ordersCount;
    itemsSold += p.itemsSold;
    grossRevenue += p.grossRevenue;
    commissionAmount += p.commissionAmount;
    promotionCombined += payoutPromotionDisplayed(p);
    penalties += getOtherDeductionValue(p.otherDeductions, 'sellerCancelPenaltyRub');
    delivery += p.deliveryDeduction;
    netAmount += p.netAmount;
  }
  return {
    id: SELLER_PAYOUTS_TOTAL_ROW_ID,
    batchId: null,
    sellerId,
    shopName: '',
    slug: '',
    accrualPeriodStart: null,
    accrualPeriodEndExclusive: null,
    ordersCount,
    itemsSold,
    grossRevenue,
    commissionAmount,
    promotionDeduction: promotionCombined,
    deliveryDeduction: delivery,
    otherDeductions: { sellerCancelPenaltyRub: penalties },
    netAmount,
    status: '',
    paid: false,
    paidAt: null,
    externalPaymentId: null,
    comment: null,
    updDocument: null,
  };
}

function SellerPayoutsSection({ sellerId }: { sellerId: number }) {
  const handleDownloadUpdPdf = useCallback(
    async (row: SellerFinancePayoutLineDto, kind: 'act' | 'invoice') => {
      if (!row.updDocument?.id) return;
      const publicUrl =
        kind === 'invoice'
          ? row.updDocument.invoicePdfPublicUrl
          : row.updDocument.pdfPublicUrl;
      if (publicUrl) {
        window.open(publicUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      try {
        const blob = await sellerFinanceApi.downloadUpdPdfBlob(
          row.updDocument.id,
          kind,
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${kind === 'invoice' ? 'schet-faktura' : 'akt'}-${row.updDocument.documentNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        // Ошибка сети/доступа — детали в Network.
      }
    },
    [],
  );

  const payoutColumns = useMemo<OptionsTableColumn<SellerFinancePayoutLineDto>[]>(
    () => [
      {
        key: 'period',
        label: 'Период (UTC)',
        style: { whiteSpace: 'nowrap' },
        render: (p) =>
          emphasizeTotal(
            p,
            isPayoutTotalsRow(p) ? (
              'Итого'
            ) : p.accrualPeriodStart && p.accrualPeriodEndExclusive ? (
              `${formatIsoDate(p.accrualPeriodStart)} — ${formatIsoDate(p.accrualPeriodEndExclusive)}`
            ) : (
              '—'
            ),
          ),
      },
      {
        key: 'ordersCount',
        label: 'Заказов',
        render: (p) => emphasizeTotal(p, p.ordersCount),
      },
      {
        key: 'itemsSold',
        label: 'Продано шт.',
        render: (p) => emphasizeTotal(p, p.itemsSold),
      },
      {
        key: 'grossRevenue',
        label: 'Выручка',
        render: (p) => emphasizeTotal(p, formatPrice(p.grossRevenue)),
      },
      {
        key: 'commissionAmount',
        label: 'Комиссия',
        render: (p) => emphasizeTotal(p, formatPrice(p.commissionAmount)),
      },
      {
        key: 'promotionDeduction',
        label: 'Продвижение',
        render: (p) => emphasizeTotal(p, formatPrice(payoutPromotionDisplayed(p))),
      },
      {
        key: 'sellerCancelPenalty',
        label: 'Штрафы',
        render: (p) =>
          emphasizeTotal(
            p,
            formatPrice(getOtherDeductionValue(p.otherDeductions, 'sellerCancelPenaltyRub')),
          ),
      },
      {
        key: 'deliveryDeduction',
        label: 'Доставка',
        render: (p) => emphasizeTotal(p, formatPrice(p.deliveryDeduction)),
      },
      {
        key: 'netAmount',
        label: 'К выплате',
        render: (p) => emphasizeTotal(p, formatPrice(p.netAmount)),
      },
      {
        key: 'paid',
        label: 'Выплачено',
        render: (p) =>
          emphasizeTotal(p, isPayoutTotalsRow(p) ? '—' : p.paid ? 'Да' : 'Нет'),
      },
      {
        key: 'paidAt',
        label: 'Дата',
        render: (p) =>
          emphasizeTotal(p, !isPayoutTotalsRow(p) && p.paidAt ? formatIsoDate(p.paidAt) : '—'),
      },
      {
        key: 'updPdf',
        label: 'Документы',
        render: (p) =>
          emphasizeTotal(
            p,
            !isPayoutTotalsRow(p) && p.updDocument?.id ? (
              <Group gap={8} wrap="nowrap">
                <Anchor
                  href="#"
                  size="sm"
                  onClick={(event) => {
                    event.preventDefault();
                    void handleDownloadUpdPdf(p, 'act');
                  }}
                >
                  Акт
                </Anchor>
                <Anchor
                  href="#"
                  size="sm"
                  onClick={(event) => {
                    event.preventDefault();
                    void handleDownloadUpdPdf(p, 'invoice');
                  }}
                >
                  Счёт-фактура
                </Anchor>
              </Group>
            ) : (
              '—'
            ),
          ),
      },
    ],
    [handleDownloadUpdPdf],
  );

  const { data: payouts = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['seller-finance', 'seller-payouts', sellerId],
    queryFn: () => sellerFinanceApi.listSellerPayouts(sellerId),
  });

  const payoutRows = useMemo(() => {
    if (payouts.length === 0) return payouts;
    return [...payouts, buildSellerPayoutsTotalsRow(payouts, sellerId)];
  }, [payouts, sellerId]);

  return (
    <section className="seller-dashboard-card seller-finance-card">
      <div className="seller-dashboard-card__head">
        <h2 className="seller-dashboard-card__title">История выплат</h2>
      </div>
      <p className="seller-finance-note">
        Период начислений по заказам (UTC), продвижение, комиссия, штрафы, доставка и сумма к выплате.
        В конце таблицы — суммы по столбцам.
      </p>
      <div className="seller-finance-table-wrap">
      <OptionsTable<SellerFinancePayoutLineDto>
        columns={payoutColumns}
        data={payoutRows}
        loading={isLoading}
        error={isError ? new Error('Не удалось загрузить данные') : undefined}
        refetch={() => void refetch()}
        emptyMessage="Выплат пока нет."
      />
      </div>
    </section>
  );
}

export default function SellerDetailPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as SellerDetailLocationState | undefined;
  const returnToProductId =
    typeof locationState?.returnToProductId === 'number' ? locationState.returnToProductId : undefined;
  const returnToOrderId =
    typeof locationState?.orderId === 'number' ? locationState.orderId : undefined;

  const handleBack = () => {
    if (locationState?.from === 'seller-finance') {
      navigate('/seller-finance');
      return;
    }
    if (locationState?.from === 'reviews') {
      navigate('/reviews');
      return;
    }
    if (locationState?.from === 'order' && returnToOrderId != null) {
      navigate(`/orders/${returnToOrderId}`);
      return;
    }
    if (returnToProductId != null) {
      navigate(`/products/${returnToProductId}`, {
        state:
          locationState?.returnToProductFrom != null && locationState.returnToProductFrom !== ''
            ? { from: locationState.returnToProductFrom }
            : undefined,
      });
      return;
    }
    navigate('/sellers');
  };

  const { sellerId, applicationId } = useParams<{ sellerId?: string; applicationId?: string }>();
  const [rejectKind, setRejectKind] = useState<SellerApplicationRejectionKind>('POOR_DOCUMENT_PHOTOS');
  const [rejectComment, setRejectComment] = useState('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState<SellerAccountBlockReasonDto>('RUDE_CUSTOMER_CONTACT');
  const [blockComment, setBlockComment] = useState('');
  const sellerNumericId = sellerId ? parseInt(sellerId, 10) : NaN;
  const applicationNumericId = applicationId ? parseInt(applicationId, 10) : NaN;
  const isApplicationOnlyRoute = Number.isFinite(applicationNumericId) && applicationNumericId > 0;
  const isSellerRoute = Number.isFinite(sellerNumericId) && sellerNumericId > 0;

  const { data: seller, isLoading, isError } = useQuery({
    queryKey: ['seller', sellerNumericId],
    queryFn: () => sellersApi.getById(sellerNumericId),
    enabled: isSellerRoute,
  });

  const {
    data: applicationOnly,
    isLoading: isApplicationLoading,
    isError: isApplicationError,
  } = useQuery({
    queryKey: ['seller-application-moderation', applicationNumericId],
    queryFn: () => sellerApplicationsModerationApi.getOne(applicationNumericId),
    enabled: isApplicationOnlyRoute,
  });
  const pickupPointsQueryEnabled =
    isSellerRoute ||
    (isApplicationOnlyRoute &&
      !!applicationOnly &&
      (applicationOnly.sellerId != null || applicationOnly.seller != null));

  const { data: allPickupPoints = [], isLoading: isPickupPointsLoading } = useQuery({
    queryKey: ['pickup-points-managed'],
    queryFn: () => pickupPointsApi.getAll(),
    enabled: pickupPointsQueryEnabled,
  });

  const app: (SellerApplicationRow & Record<string, unknown>) | null = isApplicationOnlyRoute
    ? (applicationOnly ?? null)
    : ((seller?.application as SellerApplicationRow & Record<string, unknown>) ?? null);

  const appId = app?.id ?? null;
  const appData = (app ?? {}) as Record<string, unknown>;
  const accountTypeKey = getText(appData, 'accountType');
  const accountTypeDisplay = accountTypeKey ? (ACCOUNT_LABELS[accountTypeKey] ?? accountTypeKey) : '—';

  /** Продавец из URL или привязанный к заявке (после одобрения). */
  const effectiveSellerId = seller?.id ?? app?.sellerId ?? app?.seller?.id ?? null;
  const showPickupFinanceTabs = isSellerRoute || (isApplicationOnlyRoute && effectiveSellerId != null);
  const [activeTab, setActiveTab] = useState<ProfileTabValue>(
    locationState?.tab === 'finance' ? 'finance' : 'application',
  );

  const profileTabs = useMemo((): { value: ProfileTabValue; label: string }[] => {
    const rows: { value: ProfileTabValue; label: string }[] = [...PROFILE_TABS_MAIN];
    if (showPickupFinanceTabs) {
      rows.push(...PROFILE_TABS_SELLER_EXTRA);
    }
    return rows;
  }, [showPickupFinanceTabs]);

  useEffect(() => {
    if (!profileTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(profileTabs[0]?.value ?? 'application');
    }
  }, [activeTab, profileTabs]);

  const sellerPickupPoints = allPickupPoints.filter((point) => point.sellerId === effectiveSellerId);
  const title = seller?.shopName || getText(appData, 'shopName') || (appId ? `Заявка #${appId}` : 'Карточка продавца');
  const sellerAccountBlock = seller?.accountBlock;
  const showSellerBlockAlertSidebar =
    isSellerRoute &&
    seller != null &&
    app?.status === 'APPROVED' &&
    sellerAccountBlock != null;

  const approveMut = useMutation({
    mutationFn: (id: number) => sellerApplicationsModerationApi.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-applications-moderation'] });
      queryClient.invalidateQueries({ queryKey: ['seller-application-moderation', appId] });
      queryClient.invalidateQueries({ queryKey: ['seller', sellerNumericId] });
      queryClient.invalidateQueries({ queryKey: ['sellers'] });
      notifications.show({
        title: 'Заявка одобрена',
        message: 'Статус обновлён.',
        color: 'green',
      });
    },
    onError: (e: any) => {
      notifications.show({
        title: 'Ошибка',
        message: e?.response?.data?.message || String(e),
        color: 'red',
      });
    },
  });

  const enrollTbankSeMut = useMutation({
    mutationFn: (id: number) => sellerApplicationsModerationApi.enrollTbankSe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-application-moderation', appId] });
      notifications.show({
        title: 'Т‑Банк',
        message: 'Самозанятого снова отправили в получатели выплат',
        color: 'green',
      });
    },
    onError: (e: any) => {
      notifications.show({
        title: 'Не удалось отправить в Т‑Банк',
        message: e?.response?.data?.message || String(e),
        color: 'red',
      });
    },
  });

  const rejectMut = useMutation({
    mutationFn: ({
      id,
      rejectionKind: kind,
      comment,
    }: {
      id: number;
      rejectionKind: SellerApplicationRejectionKind;
      comment?: string;
    }) =>
      sellerApplicationsModerationApi.reject(id, {
        rejectionKind: kind,
        ...(comment?.trim() ? { comment: comment.trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-applications-moderation'] });
      queryClient.invalidateQueries({ queryKey: ['seller-application-moderation', appId] });
      queryClient.invalidateQueries({ queryKey: ['seller', sellerNumericId] });
      queryClient.invalidateQueries({ queryKey: ['sellers'] });
      setRejectComment('');
      setRejectKind('POOR_DOCUMENT_PHOTOS');
      setRejectModalOpen(false);
      notifications.show({
        title: 'Заявка отклонена',
        message: 'Статус обновлён.',
        color: 'orange',
      });
    },
    onError: (e: any) => {
      notifications.show({
        title: 'Ошибка',
        message: e?.response?.data?.message || String(e),
        color: 'red',
      });
    },
  });

  const blockAccountMut = useMutation({
    mutationFn: () =>
      sellersApi.accountBlock(sellerNumericId, {
        reason: blockReason,
        ...(blockComment.trim() ? { comment: blockComment.trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller', sellerNumericId] });
      queryClient.invalidateQueries({ queryKey: ['sellers'] });
      setBlockComment('');
      setBlockReason('RUDE_CUSTOMER_CONTACT');
      setBlockModalOpen(false);
      notifications.show({
        title: 'Кабинет заблокирован',
        message: 'Продавец видит ограниченный режим в личном кабинете.',
        color: 'orange',
      });
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Ошибка',
        message: (e as { response?: { data?: { message?: string } } })?.response?.data?.message || String(e),
        color: 'red',
      });
    },
  });

  const unblockAccountMut = useMutation({
    mutationFn: () => sellersApi.accountUnblock(sellerNumericId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller', sellerNumericId] });
      queryClient.invalidateQueries({ queryKey: ['sellers'] });
      notifications.show({
        title: 'Блокировка снята',
        message: 'Доступ в кабинет восстановлен.',
        color: 'green',
      });
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Ошибка',
        message: (e as { response?: { data?: { message?: string } } })?.response?.data?.message || String(e),
        color: 'red',
      });
    },
  });

  if (!isSellerRoute && !isApplicationOnlyRoute) {
    return (
      <div className="page-container seller-detail-page">
        <Text c="red">Некорректный идентификатор</Text>
      </div>
    );
  }

  if (isLoading || isApplicationLoading || isPickupPointsLoading) {
    return (
      <div className="page-container seller-detail-page">
        <Text>Загрузка…</Text>
      </div>
    );
  }

  if ((isSellerRoute && (isError || !seller)) || (isApplicationOnlyRoute && (isApplicationError || !applicationOnly))) {
    return (
      <div className="page-container seller-detail-page">
        <Text c="red">Данные не найдены</Text>
      </div>
    );
  }

  return (
    <div className="page-container seller-detail-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Продавцы', path: '/sellers' },
          { label: title },
        ]}
      />

      <div className="page-header">
        <div className="seller-detail-page-header-row">
          <div className="seller-detail-page-header-left">
            <BackButton onClick={handleBack} />
            <h1 className="seller-detail-page-header-title">{title}</h1>
          </div>
        </div>
      </div>

      <Modal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Отказ в регистрации"
        maxWidth={440}
        styles={{
          content: {
            width: 'min(440px, calc(100vw - 32px))',
            maxWidth: 'min(440px, calc(100vw - 32px))',
          },
        }}
      >
        <div className="seller-detail-block-modal">
          <SharedSelect
            label="Причина отказа"
            data={SELLER_APPLICATION_REJECTION_OPTIONS.map(({ value, label }) => ({
              value,
              label,
            }))}
            value={rejectKind}
            onChange={(v) => v && setRejectKind(String(v) as SellerApplicationRejectionKind)}
          />
          <Textarea
            label="Комментарий для продавца"
            placeholder="Необязательно: уточнение к выбранной причине"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <div className="seller-detail-block-modal__actions">
            <Button variant="secondary" onClick={() => setRejectModalOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={rejectMut.isPending}
              onClick={() => {
                if (appId == null) return;
                rejectMut.mutate({
                  id: appId,
                  rejectionKind: rejectKind,
                  comment: rejectComment,
                });
              }}
            >
              Отказать
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={blockModalOpen}
        onClose={() => setBlockModalOpen(false)}
        title="Блокировка кабинета"
        maxWidth={440}
      >
        <div className="seller-detail-block-modal">
          <SharedSelect
            label="Причина"
            data={SELLER_ACCOUNT_BLOCK_SELECT_OPTIONS.map(({ value, label }) => ({
              value,
              label,
            }))}
            value={blockReason}
            onChange={(v) => v && setBlockReason(String(v) as SellerAccountBlockReasonDto)}
          />
          <p className="seller-detail-block-modal__hint">
            Для причин «до выяснения обстоятельств» срок автоматической разблокировки не
            назначается: продавец может обратиться в поддержку. При «навсегда» доступ не
            восстанавливается без действий администратора.
          </p>
          <Textarea
            label="Комментарий"
            placeholder="Необязательно: пояснение для продавца"
            value={blockComment}
            onChange={(e) => setBlockComment(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <div className="seller-detail-block-modal__actions">
            <Button variant="secondary" onClick={() => setBlockModalOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={blockAccountMut.isPending}
              onClick={() => void blockAccountMut.mutate()}
            >
              Заблокировать
            </Button>
          </div>
        </div>
      </Modal>

      <div className="content-wrapper">
        <Stack className="seller-detail-page__body" gap="sm" w="100%" align="stretch">
          {showSellerBlockAlertSidebar && sellerAccountBlock ? (
            <Alert
              className="seller-detail-block-alert"
              tone="error"
              title="Кабинет заблокирован"
              w="100%"
              aria-label="Блокировка кабинета"
            >
              <div className="seller-detail-block-alert__lines">
                <p>
                  <strong>Причина: </strong>
                  {sellerAccountBlockReasonAdminLabel(sellerAccountBlock.reason)}
                </p>
                {sellerAccountBlock.comment?.trim() ? (
                  <p className="seller-detail-block-alert__comment">
                    <strong>Комментарий: </strong>
                    {sellerAccountBlock.comment.trim()}
                  </p>
                ) : null}
                <p>{sellerAccountBlockTimingLine(sellerAccountBlock)}</p>
              </div>
            </Alert>
          ) : null}
          <div className="seller-detail-content-tabs">
            {!app ? (
              <Stack gap="lg" w="100%">
                {!isSellerRoute || !seller ? (
                  <Text size="sm" c="dimmed">
                    Связанная заявка отсутствует.
                  </Text>
                ) : (
                  <Tabs className="seller-detail-tabs" defaultValue={'finance' satisfies ProfileTabValue} w="100%">
                    <Tabs.List className="seller-detail-tabs-list">
                      <Tabs.Tab className="seller-detail-tabs-tab" value="finance">Финансы</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel className="seller-detail-tab-panel" value="finance">
                      <SellerFinanceTab
                        sellerId={seller.id}
                        shopName={seller.shopName}
                        payoutsSection={<SellerPayoutsSection sellerId={seller.id} />}
                      />
                    </Tabs.Panel>
                  </Tabs>
                )}
              </Stack>
            ) : (
              <Stack
                gap={0}
                className={`seller-detail-page__inner${activeTab === 'finance' || activeTab === 'pickup-points' ? ' seller-detail-page__inner--wide' : ''}`}
                w="100%"
              >
                <Tabs
                  className="seller-detail-tabs"
                  value={activeTab}
                  onChange={(value) => value && setActiveTab(value as ProfileTabValue)}
                  w="100%"
                >
                  <Tabs.List className="seller-detail-tabs-list">
                    {profileTabs.map((tab) => (
                      <Tabs.Tab className="seller-detail-tabs-tab" key={tab.value} value={tab.value}>
                        {tab.label}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>

                  <Tabs.Panel className="seller-detail-tab-panel" value="application">
                    <section className="seller-profile-card seller-application-card">
                      <div className="seller-profile-identity">
                        {getText(appData, 'logoFileUrl') ? (
                          <SellerDetailLogo url={getText(appData, 'logoFileUrl')!} />
                        ) : (
                          <div className="seller-profile-logo-preview seller-profile-logo-placeholder">
                            Логотип
                          </div>
                        )}
                        <div className="seller-profile-identity__main">
                          <h2 className="seller-profile-identity__name">
                            {getText(appData, 'shopName') ?? seller?.shopName ?? '—'}
                          </h2>
                          <div className="seller-profile-identity__status">
                            <SellerAdminStatusBadges
                              status={app.status}
                              accountBlock={sellerAccountBlock}
                              profileHidden={seller?.profileHidden}
                              profileDeletion={seller?.profileDeletion}
                              showDeletionDate
                            />
                          </div>
                          <p className="seller-profile-identity__meta">
                            Заявка #{app.id}
                            {getText(appData, 'createdAt')
                              ? ` · ${formatDate(getText(appData, 'createdAt'))}`
                              : ''}
                          </p>
                          {isSellerRoute && seller ? (
                            <SellerShopRating
                              rating={seller.averageRating}
                              reviewCount={seller.approvedReviewCount}
                            />
                          ) : null}
                          <p className="seller-profile-identity__activity">
                            {getText(appData, 'activityDescription')?.trim() ||
                              'Описание деятельности не указано'}
                          </p>
                        </div>
                      </div>
                      <div className="seller-detail-actions" role="toolbar" aria-label="Действия с заявкой">
                        {app.status === 'PENDING_MODERATION' && appId ? (
                          <>
                            <button
                              type="button"
                              className="seller-detail-action seller-detail-action--accent"
                              disabled={approveMut.isPending}
                              onClick={() => approveMut.mutate(appId)}
                            >
                              Одобрить
                            </button>
                            <button
                              type="button"
                              className="seller-detail-action seller-detail-action--danger"
                              disabled={approveMut.isPending || rejectMut.isPending}
                              onClick={() => {
                                setRejectKind('POOR_DOCUMENT_PHOTOS');
                                setRejectComment('');
                                setRejectModalOpen(true);
                              }}
                            >
                              Отказать
                            </button>
                          </>
                        ) : null}
                        {isSellerRoute && seller ? (
                          <button
                            type="button"
                            className="seller-detail-action"
                            onClick={() => {
                              const params = new URLSearchParams({
                                sellerId: String(seller.id),
                              });
                              if (seller.shopName) {
                                params.set('shopName', seller.shopName);
                              }
                              navigate(`/sellers/support-chat?${params.toString()}`);
                            }}
                          >
                            В чат
                          </button>
                        ) : null}
                        {isSellerRoute && seller && app.status === 'APPROVED' ? (
                          sellerAccountBlock ? (
                            <button
                              type="button"
                              className="seller-detail-action"
                              disabled={unblockAccountMut.isPending}
                              onClick={() => unblockAccountMut.mutate()}
                            >
                              Снять блокировку
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="seller-detail-action seller-detail-action--danger"
                              onClick={() => {
                                setBlockReason('RUDE_CUSTOMER_CONTACT');
                                setBlockComment('');
                                setBlockModalOpen(true);
                              }}
                            >
                              Заблокировать
                            </button>
                          )
                        ) : null}
                      </div>
                    </section>
                    {appId != null ? (
                      <SellerProfileChangesSection
                        applicationId={appId}
                        current={{
                          shopName: seller?.shopName ?? getText(appData, 'shopName') ?? null,
                          activityDescription: getText(appData, 'activityDescription') ?? null,
                          logoFileUrl: getText(appData, 'logoFileUrl') ?? null,
                        }}
                        pending={{
                          shopName: app.pendingShopName ?? null,
                          activityDescription: app.pendingActivityDescription ?? null,
                          logoFileUrl: app.pendingLogoFileUrl ?? null,
                        }}
                        submittedAt={app.profileChangesSubmittedAt ?? null}
                        onModerated={() => {
                          queryClient.invalidateQueries({ queryKey: ['seller-application-moderation', appId] });
                          queryClient.invalidateQueries({ queryKey: ['seller', sellerNumericId] });
                          queryClient.invalidateQueries({ queryKey: ['sellers'] });
                          queryClient.invalidateQueries({ queryKey: ['seller-profile-changes-moderation'] });
                        }}
                      />
                    ) : null}
                    {getText(appData, 'moderationRejectionReason') ? (
                      <Section title="Причина отказа">
                        <p className="seller-profile-identity__activity">
                          {getText(appData, 'moderationRejectionReason')}
                        </p>
                      </Section>
                    ) : null}
                  </Tabs.Panel>

                  <Tabs.Panel value="requisites" className="seller-detail-tab-panel">
                    <Section title="Реквизиты">
                      <Row label="Тип организации" value={accountTypeDisplay} />
                      {getText(appData, 'accountType') === 'SELF_EMPLOYED' ? (
                        <>
                          <Row
                            label="Партнёр Т‑Банк / «Мой налог»"
                            value={tbankSePartnerLabel(
                              getText(appData, 'tbankSeRecipientStatus'),
                            )}
                          />
                          {getText(appData, 'tbankSeAddError') ? (
                            <Row
                              label="Ошибка T-API"
                              value={getText(appData, 'tbankSeAddError')}
                            />
                          ) : null}
                          {app?.status === 'APPROVED' &&
                            getText(appData, 'tbankSeRecipientStatus') !== 'ACTIVE' ? (
                            <Row
                              label=""
                              unstyledValue
                              value={
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  loading={enrollTbankSeMut.isPending}
                                  onClick={() => {
                                    if (appId) enrollTbankSeMut.mutate(appId);
                                  }}
                                >
                                  Отправить в Т‑Банк ещё раз
                                </Button>
                              }
                            />
                          ) : null}
                        </>
                      ) : null}
                      <Row label="ИНН" value={getText(appData, 'inn')} />
                      {getText(appData, 'accountType') === 'LEGAL_ENTITY' ? (
                        <Row label="КПП" value={getText(appData, 'kpp')?.trim() || '—'} />
                      ) : null}
                      <Row label="ОГРН" value={getText(appData, 'ogrn')} />
                      <Row label="Наименование" value={getText(appData, 'companyName')} />
                      <Row label="Юридический адрес" value={getText(appData, 'legalAddress')} />
                      <Row label="ФИО руководителя" value={getText(appData, 'directorFullName')} />
                    </Section>
                    <Section title="Контакты">
                      <Row
                        label="Телефон регистрации"
                        value={
                          formatPhoneNumber(
                            (appData.user as { phone?: string | null } | null | undefined)?.phone,
                          ) || '—'
                        }
                      />
                      <Row
                        label="Телефон директора"
                        value={formatPhoneNumber(getText(appData, 'contactPhone')) || '—'}
                      />
                      <Row
                        label="Email регистрации"
                        value={
                          (
                            appData.user as
                            | { email?: string | null }
                            | null
                            | undefined
                          )?.email?.trim() || '—'
                        }
                      />
                      <Row
                        label="Email директора"
                        value={getText(appData, 'contactEmail') ?? '—'}
                      />
                    </Section>
                    <Section title="Банковские данные">
                      <Row label="Название банка" value={getText(appData, 'bankName')} />
                      <Row label="Корр. счёт" value={getText(appData, 'correspondentAccount')} />
                      <Row label="БИК" value={getText(appData, 'bik')} />
                      <Row label="Расчётный счёт" value={getText(appData, 'bankAccountNumber')} />
                    </Section>
                  </Tabs.Panel>

                  <Tabs.Panel value="documents" className="seller-detail-tab-panel">
                    <Section title="Документы">
                      <div className="seller-profile-docs-grid">
                        <DocTilesFromUrls
                          title="Выписка ЕГРЮЛ / ЕГРИП"
                          urls={getStringArray(appData, 'egrulExtractFileUrls')}
                          applicationId={appId}
                        />
                        <DocTilesFromUrls
                          title="Скан паспорта"
                          urls={getStringArray(appData, 'passportScanFileUrls')}
                          applicationId={appId}
                        />
                        <DocTile
                          applicationId={appId}
                          title="Договор-оферта"
                          meta={
                            getText(appData, 'offerPdfFileUrl')
                              ? [
                                getText(appData, 'offerAcceptedAt')
                                  ? `подписан ${formatDate(getText(appData, 'offerAcceptedAt'))}`
                                  : app.offerAccepted
                                    ? 'принята'
                                    : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || 'PDF'
                              : null
                          }
                          href={getText(appData, 'offerPdfFileUrl')}
                        />
                      </div>
                    </Section>
                  </Tabs.Panel>
                  <Tabs.Panel value="pickup-points" className="seller-detail-tab-panel">
                    <Section title="Точки самовывоза">
                      {effectiveSellerId == null ? (
                        <Text size="sm" c="dimmed">
                          Для заявки без созданного продавца точки самовывоза отсутствуют.
                        </Text>
                      ) : sellerPickupPoints.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          У продавца пока нет точек самовывоза.
                        </Text>
                      ) : (
                        <div className="pickup-points-grid">
                          {sellerPickupPoints.map((point) => {
                            const raw = point.metroIconColor?.trim();
                            const metroColor = !raw ? 'var(--accent, #ff8338)' : raw.startsWith('#') ? raw : `#${raw}`;
                            const inactive = point.isOperationallyInactive === true;
                            return (
                              <article key={point.id} className="pickup-point-card">
                                <span
                                  className={
                                    inactive
                                      ? 'pickup-point-card__status pickup-point-card__status--off'
                                      : 'pickup-point-card__status pickup-point-card__status--on'
                                  }
                                >
                                  {inactive ? 'Не работает' : 'Активна'}
                                </span>
                                <div className="pickup-point-card__header">
                                  {point.city ? (
                                    <div className="pickup-point-card__city">{point.city}</div>
                                  ) : null}
                                  <h3 className="pickup-point-card__title">{point.address}</h3>
                                  {point.metro ? (
                                    <div className="pickup-point-card__metro">
                                      <svg
                                        className="pickup-point-card__metro-icon"
                                        width="14"
                                        height="10"
                                        viewBox="0 0 14 10"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden
                                        style={{ ['--metro-fill' as string]: metroColor }}
                                      >
                                        <path d="M9.75441 0L13.0328 8.60245L14 8.6052V9.89831H9.04351V8.6052H9.77423L9.06333 6.46832L6.99009 10L4.91685 6.46832L4.20595 8.6052H4.93667V9.89831H0V8.6052H0.947429L4.22577 0L6.99009 5.02954L9.75441 0Z" />
                                      </svg>
                                      <span>{point.metro}</span>
                                    </div>
                                  ) : null}
                                </div>
                                <div className="pickup-point-card__meta">
                                  <div className="pickup-point-card__row">
                                    <IoCarOutline size={15} aria-hidden />
                                    <span>
                                      {point.deliveryProvider === 'SELLER'
                                        ? 'Силами продавца'
                                        : 'Партнерами маркетплейса'}
                                    </span>
                                  </div>
                                  {point.phoneNumber ? (
                                    <div className="pickup-point-card__row">
                                      <IoCallOutline size={15} aria-hidden />
                                      <span>{formatPhoneNumber(point.phoneNumber)}</span>
                                    </div>
                                  ) : null}
                                  {point.email ? (
                                    <div className="pickup-point-card__row">
                                      <IoMailOutline size={15} aria-hidden />
                                      <span>{point.email}</span>
                                    </div>
                                  ) : null}
                                  {point.telegram ? (
                                    <div className="pickup-point-card__row">
                                      <IoChatbubbleOutline size={15} aria-hidden />
                                      <span>{point.telegram}</span>
                                    </div>
                                  ) : null}
                                  {point.workingHoursFormatted && point.workingHoursFormatted.length > 0 ? (
                                    <div className="pickup-point-card__hours">
                                      <IoTimeOutline size={15} className="pickup-point-card__hours-icon" aria-hidden />
                                      <div className="pickup-point-card__hours-list">
                                        {point.workingHoursFormatted.map((timeStr, index) => (
                                          <div key={`${point.id}-wh-${index}`}>{timeStr}</div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </Section>
                  </Tabs.Panel>

                  <Tabs.Panel value="finance" className="seller-detail-tab-panel">
                    {effectiveSellerId != null ? (
                      <SellerFinanceTab
                        sellerId={effectiveSellerId}
                        shopName={seller?.shopName ?? getText(appData, 'shopName') ?? null}
                        payoutsSection={<SellerPayoutsSection sellerId={effectiveSellerId} />}
                      />
                    ) : (
                      <Text size="sm" c="dimmed">
                        Карточка продавца не привязана — финансы недоступны.
                      </Text>
                    )}
                  </Tabs.Panel>
                </Tabs>
              </Stack>
            )}
          </div>
        </Stack>
      </div>
    </div>
  );
}
